import { z } from 'zod';
/**
 * **Who may text a guest, when, and about what** — spec §2.6's config, and the
 * two features `docs/lovespa-gaps-plan.md` E1/E2 hang off it.
 *
 * Everything in this file is a *policy document*, not a message and not a
 * schedule. The arithmetic that turns "the evening before at 18:00" into an
 * instant lives in `apps/notifications` (which owns the tenant's timezone and
 * `packages/scheduling`'s DST-proven helpers); the sentences live in
 * `apps/notifications/src/sms/templates.ts` with the other Domain C text. What
 * is here is the vocabulary all three share, so that an operator's CLI, a
 * database reader and a scheduler cannot disagree about what a tenant asked
 * for.
 *
 * ## Everything defaults to OFF, and that is the design
 *
 * Every tenant in the fleet today sends no reminders and no owner alerts. A
 * default that turned either on would be this file deciding, in one commit, to
 * text several hundred people who never asked to hear from us — the exact shape
 * migration 0032 refused when it declined to backfill a mailing list ("inventing
 * consent"). So `enabled` is `false` unless a document says otherwise, an
 * unreadable document reads as OFF, and the *shape* of the defaults below is
 * only ever what a tenant gets **when an operator turns the feature on**.
 */
/* ------------------------------------------------------------------ */
/* Who owns guest messaging (spec §2.6)                                */
/* ------------------------------------------------------------------ */
/**
 * `tenant_config.config.guest_messaging`, named exactly as spec §2.6 names it.
 *
 * §2.6's argument in one sentence: "Both sides of the boundary can text a
 * guest… If both do, the guest gets two reminders from two numbers, and a STOP
 * sent to one is unknown to the other — an annoyance that is also a TCPA
 * compliance failure." So ownership is explicit, per tenant, and singular.
 *
 *  - `yourstore` — we send guest reminders from the tenant's own sender, and
 *    STOP is handled by us. **The default**, because it is the de-facto state:
 *    every guest-facing message this platform sends today (the booking-site
 *    OTP, the shipment notice, "your photos are ready") comes from here, and no
 *    tenant has a concierge that owns messaging yet.
 *  - `partner` — a conversational partner owns the spa's number and answers it.
 *    §2.6: "yourstore sends no guest SMS or email at all for that tenant, and
 *    the partner is the authority on messaging consent". Guest reminders are
 *    therefore *not sent from here*, whatever `reminders.enabled` says.
 *  - `off` — nobody sends guest messages. A tenant that has not decided, or one
 *    that has asked us to stop.
 *
 * **It does not narrow what is always ours.** §2.6's table is explicit: partner
 * webhooks, staff alerts and the booking-site OTP are "always yourstore". Owner
 * alerts (E2) are *staff* alerts — the tenant's own phone, about the tenant's
 * own book — so they are unaffected by this field, and there is a test that
 * says so rather than a comment that hopes so.
 */
export const GuestMessagingOwner = z.enum(['yourstore', 'partner', 'off']);
/* ------------------------------------------------------------------ */
/* Days and times, as a person writes them                             */
/* ------------------------------------------------------------------ */
/**
 * Monday-first weekday **names**, and the names are the point.
 *
 * `primitives.ts` already carries `Weekday`, which is an integer 0–6 — right for
 * `WeekHours`, which is a seven-element array the importer copies out of
 * LoveSpa. This is the other thing: a value an **operator types** and a console
 * will one day render as checkboxes. The v1.7 lesson is that an integer on a
 * human-edited surface is a silent off-by-one waiting for the first person who
 * assumes Sunday is 0 — and "she does not text on Sundays"
 * (`docs/lovespa-discovery.md` §6) turning into "she does not text on Mondays"
 * is a bug nobody would find for a week.
 *
 * Lowercase and English because this is a *config key*, not display text; the
 * day a console shows it, it translates the name the way it translates
 * everything else.
 */
export const WEEKDAY_NAMES = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
];
export const WeekdayName = z.enum(WEEKDAY_NAMES);
/** `weekdayIndex()` in `packages/scheduling` is Monday-first too; this is its name. */
export function weekdayNameFromIndex(index) {
    return WEEKDAY_NAMES[((index % 7) + 7) % 7];
}
/** `HH:MM`, 24-hour, read in the **tenant's** timezone. Never a UTC offset. */
export const LocalTimeOfDay = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'must be HH:MM in 24-hour local time');
/**
 * A window of the local day in which a message is **not** sent.
 *
 * It may wrap midnight, and the usual case does: `21:00`–`08:00` is one window
 * that spans two calendar days. So this is not an interval in the
 * `packages/scheduling` sense and deliberately does not reuse `Interval` — half-
 * open `[start, end)` over instants is the right model for a booking and the
 * wrong one for "after nine at night, wherever the tenant is".
 */
export const QuietHours = z.object({
    /** When the quiet begins, local. */
    start: LocalTimeOfDay,
    /** When it ends, local. Earlier than `start` means the window wraps midnight. */
    end: LocalTimeOfDay,
});
/* ------------------------------------------------------------------ */
/* E1 — guest reminders                                                */
/* ------------------------------------------------------------------ */
/**
 * **When a reminder goes out**, relative to the appointment.
 *
 * Two shapes, because the owner of Love Spa named one of them and the platform
 * owes the other:
 *
 *  - `evening_before` — `docs/lovespa-discovery.md` §2.7: "She reminds every
 *    client **the night before**". It is a *wall-clock* rule, not a duration: a
 *    3 pm appointment and a 9 am one are both reminded at 18:00 the previous
 *    evening, which is what "the night before" means to the person saying it.
 *    Expressing it as "21 hours before" would be a different message for every
 *    booking and would drift by an hour twice a year.
 *  - `hours_before` — the duration rule, for the tenants who want "two hours
 *    before" (a restaurant's table, a tour's departure). Fractional hours are
 *    admitted because `0.5` is a real answer for a walk-up business.
 *
 * A discriminated union rather than two nullable fields: they are different
 * kinds of arithmetic (one needs the tenant's calendar, the other does not), and
 * a document that carried both would have to say which wins.
 */
export const ReminderOffset = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('evening_before'),
        /** Local wall-clock time on the day *before* the appointment. */
        at: LocalTimeOfDay,
    }),
    z.object({
        kind: z.literal('hours_before'),
        /** Whole or fractional hours before the appointment's start. */
        hours: z.number().min(0.25).max(336),
    }),
]);
/**
 * The stable identity of an offset, which is the second half of the idempotency
 * guarantee (`appointment_reminders` is unique on
 * `(tenant_id, appointment_id, offset_key)`).
 *
 * It is derived from the offset's *meaning* rather than from its position in
 * the array, and that is the whole reason this function exists. An index would
 * make "the tenant added a second offset at the front" retroactively rename
 * every pending row — which is to say, send every reminder twice. A value key
 * makes adding an offset add rows, and removing one leave the rows it already
 * owed.
 */
export function reminderOffsetKey(offset) {
    return offset.kind === 'evening_before'
        ? `evening_before@${offset.at}`
        : `hours_before@${String(offset.hours)}`;
}
/** What a tenant turning reminders on gets when it names nothing: her rule. */
export const DEFAULT_REMINDER_OFFSET = { kind: 'evening_before', at: '18:00' };
/**
 * The guest's quiet window, and it is **on by default** when reminders are —
 * the one default in this file that is not "off".
 *
 * The reason is not politeness. The TCPA's own calling window is 8 am to 9 pm in
 * the *recipient's* local time, and while a reminder about a guest's own
 * appointment is transactional, a text that arrives at 3 am is the kind of thing
 * that ends with a business being asked to explain itself. `21:00`–`08:00` is
 * that window, expressed as the hours in which we do not send. A tenant may
 * widen or narrow it; a tenant cannot accidentally not have one.
 */
export const DEFAULT_REMINDER_QUIET_HOURS = { start: '21:00', end: '08:00' };
export const TenantRemindersConfig = z.object({
    /** `false` unless somebody said otherwise. Every existing tenant is unchanged. */
    enabled: z.boolean().default(false),
    /**
     * One row per reminder a confirmed appointment owes. Capped at four because
     * this is a list of *texts a guest receives about one appointment*, and the
     * fifth is not a reminder, it is nagging.
     */
    offsets: z.array(ReminderOffset).max(4).default([DEFAULT_REMINDER_OFFSET]),
    /**
     * `sms` and only `sms`, spelled out rather than assumed. There is no email
     * infrastructure in this system at all (`docs/lovespa-gaps-plan.md` E3 is
     * blocked on the owner choosing a provider), so a channel enum with one member
     * is the honest way to say "when email lands, this is where it is named" —
     * and a document that says `channel: 'email'` reads as OFF rather than
     * silently sending a text.
     */
    channel: z.literal('sms').default('sms'),
    /** See `DEFAULT_REMINDER_QUIET_HOURS`. */
    quiet_hours: QuietHours.default(DEFAULT_REMINDER_QUIET_HOURS),
});
/* ------------------------------------------------------------------ */
/* E2 — owner alerts                                                   */
/* ------------------------------------------------------------------ */
/**
 * The event types an owner may be told about.
 *
 * These are the **real** `events.type` strings — read off the emitters rather
 * than invented here, because a vocabulary that disagreed with the outbox would
 * be a feature that silently never fires:
 * `repos/bookings.ts` writes `booking.created`, `booking.updated` (a reschedule
 * *is* an update — there is no `booking.rescheduled`) and `booking.cancelled`;
 * `apps/guest-api/src/routes/inquiries.ts` writes `inquiry.received`;
 * `repos/orders.ts` writes `order.created` (there is no `order.placed`);
 * `repos/reservations.ts` writes `reservation.created` (C1 — a jar promised on
 * the website and paid for at the counter).
 *
 * `reservation.created` is emitted with **`partner: false`** and that is a fact
 * about the *outbox*, not about this list: no version of the provider agreement
 * names a reservation, so the event mints no `webhook_deliveries` row. The owner
 * alert reads `events` directly and is unaffected — which is the whole reason
 * the two mechanisms are separate.
 *
 * `booking.requested` was named here before anything emitted it — deliberately,
 * because the owner asked for exactly this alert ("booking made / requested")
 * and naming it in advance meant that the day workstream A5 landed, an owner
 * who was already subscribed started being told, with no config migration and
 * no second deploy. A5 landed on 2026-09-20 (migration 0040) and
 * `repos/requests.ts` now emits it, so the list is what it was always for.
 *
 * The **rest** of that lifecycle is not here and that is the decision:
 * `booking.request_declined`, `booking.request_proposed` and
 * `booking.request_expired` are the owner's own answers and their consequences,
 * and texting somebody about a decision they just made on the screen in front
 * of them is the failure `appointments.source in ('staff','walk_in')` already
 * exists to prevent. Confirming is the one seam left: it emits the ordinary
 * `booking.created`, which *is* in this list, and the row's `source` is still
 * the guest's — so she is told "new booking" about a booking she just made
 * herself. It needs an `actor` on the event to fix and is an open row in
 * `docs/lovespa-gaps-plan.md`.
 */
export const OWNER_ALERT_KINDS = [
    'booking.created',
    'booking.requested',
    'booking.updated',
    'booking.cancelled',
    'inquiry.received',
    'order.created',
    'reservation.created',
    /**
     * **Somebody asked for a gift card** (owner's ruling 1, migration 0051).
     *
     * In the list because it is the one thing on it that *cannot* be seen by
     * looking at the calendar: a booking and an order both land somewhere an
     * owner is already looking, and a gift request lands on a screen she has no
     * reason to open. It is emitted with **`partner: false`** — the agreement has
     * no word for a gift request, and migration 0051 grants `provider_api`
     * nothing on the table at all — so this list is the only place it goes.
     */
    'gift.requested',
    /**
     * **The 08:30 was called off, and here is who was told** (verticals-plan
     * V7 / D4). One per *call*, never per booking: the cascade's forty
     * `booking.cancelled` events are the partner's and would buzz her forty
     * times; what she needs is one line — the sailing, the reason, and how many
     * of its guests the platform could text versus could not (no consent, no
     * number). Emitted with **`partner: false`**, beside the sailing's own
     * `departure.cancelled`, whose bytes stay the agreement's.
     */
    'departure.weather_call',
];
export const OwnerAlertKind = z.enum(OWNER_ALERT_KINDS);
/**
 * `immediate` is what she asked for first; `digest` is what she asked for next.
 *
 * `docs/lovespa-discovery.md` §2.6, in her words: *"It'll come up on my calendar
 * here and here? … and I get a text"* — later: *a digest, "five new things", not
 * a buzz each time*. Both, because which one a business wants is a function of
 * how many bookings a day it takes, and that changes.
 */
export const OwnerAlertMode = z.enum(['immediate', 'digest']);
/**
 * How long a burst is coalesced in `immediate` mode, in seconds.
 *
 * A party of four booked through the site is one guest pressing one button and
 * up to four `booking.created` events; four buzzes is the failure the digest
 * exists to avoid, arriving in immediate mode where nobody asked for it. Sixty
 * seconds is short enough that "immediate" is still true and long enough to
 * catch a transaction's worth.
 */
export const OWNER_ALERT_COALESCE_SECONDS = 60;
export const TenantOwnerAlertsConfig = z.object({
    /** `false` unless somebody said otherwise. */
    enabled: z.boolean().default(false),
    /**
     * Where the text goes — E.164, the owner's own phone, and **not** read from
     * the guest book. An owner is a person we have a business relationship with
     * and a number an operator typed; going and finding "the owner's client row"
     * would be the platform guessing at a staff member's personal number out of a
     * table of customers.
     *
     * A list because a two-person spa has two phones. Capped at five, which is the
     * point past which this has become a broadcast and wants a different feature.
     */
    to: z
        .array(z.string().regex(/^\+[1-9]\d{6,14}$/, 'must be an E.164 number'))
        .max(5)
        .default([]),
    mode: OwnerAlertMode.default('immediate'),
    /**
     * The digest window. `>= 30` because a digest every five minutes is a buzz
     * each time wearing a digest's clothes, and the whole point of the feature is
     * the owner's sentence about not being interrupted.
     */
    digest_every_minutes: z.number().int().min(30).max(1440).default(120),
    /**
     * Days on which nothing is sent — *"she does not text on Sundays"*
     * (`docs/lovespa-discovery.md` §6). Names, never integers; see `WeekdayName`.
     * What accumulates on a quiet day is not dropped: it is held and delivered as
     * one digest at the next allowed moment.
     */
    quiet_days: z.array(WeekdayName).max(7).default([]),
    /**
     * Hours on which nothing is sent, local. Absent means none — and unlike the
     * guest's window this one has **no default**, because an owner alert goes to
     * the business's own phone about the business's own book, and a platform that
     * decided on its behalf when it may hear from its own diary would be deciding
     * something that is not ours. (The guest's window has a default for the
     * opposite reason: the recipient did not choose this software.)
     */
    quiet_hours: QuietHours.optional(),
    /** Which of `OWNER_ALERT_KINDS` this tenant wants. Default: all of them. */
    kinds: z.array(OwnerAlertKind).default([...OWNER_ALERT_KINDS]),
});
/* ------------------------------------------------------------------ */
/* Tolerant readers                                                    */
/* ------------------------------------------------------------------ */
/**
 * The config document as a bag of unknowns.
 *
 * `Partial<TenantConfig>` is what every reader in `tenant.ts` takes, and every
 * one of them immediately widens it the same way, because the interesting case
 * is precisely the key the *type* says cannot be there — a document written
 * before this field existed, or edited by hand with `psql`.
 */
function document(config) {
    return config ?? null;
}
function record(value) {
    return value === null || typeof value !== 'object' || Array.isArray(value)
        ? null
        : value;
}
/**
 * Field-by-field salvage, `staffBrandingOf`'s shape and `staffBrandingOf`'s
 * reason: the store **casts** `tenant_config.config` rather than parsing it, so
 * a document may predate any field here or may have been hand-edited with
 * `psql`. One bad field costs its own field and nothing else.
 *
 * The asymmetry with `staffBrandingOf` is which way a salvage fails safe. There,
 * a bad value costs a colour. Here it costs (or, worse, invents) a text message,
 * so the whole-object fallback is the **schema's defaults**, and every one of
 * those is off, quiet or empty.
 */
function salvage(schema, raw) {
    const doc = record(raw);
    if (doc === null)
        return null;
    const out = {};
    for (const [key, field] of Object.entries(schema.shape)) {
        const value = doc[key];
        if (value === undefined)
            continue;
        if (field.safeParse(value).success)
            out[key] = value;
    }
    const parsed = schema.safeParse(out);
    return parsed.success ? parsed.data : null;
}
/**
 * This tenant's reminder policy, or `null` when it has none.
 *
 * `null` — not the defaults — so a caller can tell "never configured" from
 * "configured and switched off", which are the same behaviour today and
 * different facts for an operator reading a CLI.
 */
export function remindersConfigOf(config) {
    return salvage(TenantRemindersConfig, document(config)?.['reminders']);
}
/** This tenant's owner-alert policy, or `null` when it has none. */
export function ownerAlertsConfigOf(config) {
    return salvage(TenantOwnerAlertsConfig, document(config)?.['owner_alerts']);
}
/**
 * Who owns guest messaging for this tenant (spec §2.6).
 *
 * `orderTimingOf`'s shape and its reason, with the default chosen the way
 * `guestBookingOf` chose `open`: it has to be **today's behaviour**. Every
 * guest-facing message the platform sends right now comes from here, so
 * `yourstore` is what an absent (or unreadable, or hand-mangled) field means.
 * A default of `partner` would have silently made this the answer for tenants
 * who have no partner at all.
 */
export function guestMessagingOf(config) {
    const raw = document(config)?.['guest_messaging'];
    const parsed = GuestMessagingOwner.safeParse(raw);
    return parsed.success ? parsed.data : 'yourstore';
}
/**
 * **May this platform send a guest reminder for this tenant?** — the one
 * question §2.6 exists to answer, in one place so that no surface has to
 * remember to ask it.
 *
 * Both halves are required and neither implies the other: a tenant may have
 * reminders configured and a partner that owns the channel (the answer is no,
 * and it is the partner's job now), or may own the channel and have configured
 * nothing (the answer is no, and it is nobody's job).
 */
export function remindersMaySendFrom(config) {
    const reminders = remindersConfigOf(config);
    return reminders !== null && reminders.enabled && guestMessagingOf(config) === 'yourstore';
}
/* ------------------------------------------------------------------ */
/* Consent: how a guest said yes, and when                             */
/* ------------------------------------------------------------------ */
/**
 * **How `clients.sms_opt_in` came to be true** — migration 0040.
 *
 * `sms_opt_in` was a bare boolean for the whole life of this system, and a bare
 * boolean is not consent you can defend. The question a business is actually
 * asked — by a guest, by a carrier, or by somebody with a TCPA complaint — is
 * never *"is this flag true"*. It is **when**, and **how**. So the column gained
 * two companions (`sms_opt_in_at`, `sms_opt_in_source`) and this is the closed
 * set of answers to the second.
 *
 * The values are the **paths that actually exist**, not a taxonomy:
 *
 *  - `staff` — somebody at the desk ticked the box, having asked out loud. The
 *    commonest one, and the one the owner of Love Spa will use for the two
 *    hundred people already in her book.
 *  - `guest_site` — the guest ticked an unticked box on this tenant's own site,
 *    at the identify step of a booking or a request. Express written consent in
 *    the sense the regulation means it.
 *  - `reply_yes` — the guest replied YES to an ask we sent
 *    (`sms.consent_request`), and an ask was outstanding for them. A stray
 *    "yes" arriving with no outstanding ask is **not** this, and is ignored.
 *  - `reply_stop` — the guest replied STOP. Recorded on the *withdrawal*, which
 *    is why this enum is not called "how they opted in": the pair
 *    `(at, source)` is the provenance of the **last change**, whichever
 *    direction it went, and an audit that could not say when somebody withdrew
 *    would be exactly half an audit.
 *  - `import_none` — the flag arrived from an import or a seeding script and we
 *    have **no provenance at all**. It is deliberately a value rather than
 *    `NULL`, because "we do not know how this person consented" is a fact worth
 *    being able to select for, and a `NULL` reads as "nobody has touched this
 *    row" in every other column in the schema.
 *
 * There is no `partner` value. §2.6: a tenant whose partner owns guest messaging
 * has the partner as *the authority on messaging consent*, and this column is
 * only authoritative when we own the channel — so a row recorded here for such a
 * tenant would be a second answer to a question that is not ours.
 *
 *  - `anonymized` — the flag went to `false` because the guest exercised the
 *    right to be forgotten (§7.6, `anonymizeClient` in `packages/store`). Its
 *    own value rather than `staff`, because provenance is the point of this
 *    column: "she withdrew consent at the counter" and "she asked to be
 *    forgotten and the owner ran it" are different facts, and the second one
 *    also explains why every other column on the row is blank. Migration 0061
 *    widens the CHECK to admit it.
 */
export const SMS_OPT_IN_SOURCES = [
    'staff',
    'guest_site',
    'reply_yes',
    'reply_stop',
    'import_none',
    'anonymized',
];
export const SmsOptInSource = z.enum(SMS_OPT_IN_SOURCES);
/**
 * The carrier's own keyword sets, which is the point: these are the words
 * **Twilio's Advanced Opt-Out already acts on at the carrier level**, so the
 * list here is not a policy we invented but the one we are obliged to stay in
 * step with. A guest who texts STOP is suppressed by the carrier whatever this
 * function decides; the function exists so that *we learn about it too* — which
 * is the whole of the gap spec §2.6 named ("STOP handled by us") and
 * `docs/lovespa-gaps-plan.md` recorded as ◐.
 *
 * `YES` is split out of the START set on purpose, and this is the only place
 * where this code and the carrier deliberately disagree about a word. Twilio
 * treats YES as a *resume* keyword; we treat it as an **opt-in**, and only when
 * an ask is outstanding (see `docs/yourstore-spec.md` §2.6.2). The two are
 * compatible because they act on different things: the carrier resumes
 * deliverability, we record consent. A guest who texts YES out of the blue gets
 * their deliverability back from Twilio and no `sms_opt_in` from us, which is
 * the correct pair of outcomes — a stray "yes" is not consent.
 */
const STOP_WORDS = new Set([
    'stop',
    'stopall',
    'unsubscribe',
    'cancel',
    'end',
    'quit',
    'revoke',
    'optout',
]);
const START_WORDS = new Set(['start', 'unstop']);
const YES_WORDS = new Set(['yes', 'si', 'sí']);
/**
 * Classify one inbound message body.
 *
 * **The whole body must be the keyword**, after trimming and case folding. This
 * is the carriers' own rule and it is the right one: "please stop by at 3" and
 * "can I cancel my 2 o'clock?" both contain a keyword and neither is a consent
 * withdrawal, and unsubscribing somebody who was asking to reschedule is a
 * worse failure than missing a STOP that the carrier honoured anyway.
 *
 * Punctuation is stripped because a phone's autocorrect adds a full stop, and
 * accents are folded because a Spanish-speaking guest's keyboard supplies `sí`.
 * Nothing else is normalized: this function never sees the number it applies to
 * and never returns any part of the body, so it is safe to call from a route
 * that is forbidden to log either.
 */
export function classifyInboundSms(body) {
    const word = body
        .trim()
        .toLowerCase()
        // Strip surrounding punctuation only — an interior character means this was
        // a sentence, and a sentence is not a keyword.
        .replace(/^[\p{P}\p{S}\s]+|[\p{P}\p{S}\s]+$/gu, '');
    if (word === '')
        return null;
    if (STOP_WORDS.has(word))
        return 'stop';
    if (START_WORDS.has(word))
        return 'start';
    if (YES_WORDS.has(word))
        return 'yes';
    return null;
}
/**
 * How long an "ask her by text" invitation stays answerable, in days.
 *
 * Fourteen, and the number is a consent decision rather than a timeout. A YES
 * arriving three months after the question was asked is a person replying to a
 * message they have just scrolled past, not somebody agreeing to be texted; a
 * YES the next morning plainly is. Two weeks comfortably covers "she saw it
 * when she got home from work" and comfortably excludes "she was tidying her
 * inbox".
 */
export const CONSENT_ASK_TTL_DAYS = 14;
/**
 * **Every event type whose insertion owes an `sms_deliveries` row**, in one
 * place, so that the database's list and the code's cannot drift apart.
 *
 * ## Why this constant exists
 *
 * The trigger `events_seed_sms_delivery` is what makes *commit is the enqueue*
 * true for texts (migration 0013), and it is scoped by an explicit type list
 * rather than by anything clever, deliberately: adding a message kind should be
 * a reviewed line in a migration. The cost of that choice is that the list is
 * **restated whole** on every widening — 0013 → 0028 → 0030 → 0037 → 0040 →
 * 0041 — and on 2026-09-20 two waves wrote a widening each, on one tree, and
 * the second one silently dropped the first one's type. It was caught by a
 * person reading two diffs side by side, which is not a mechanism.
 *
 * So this is the mechanism: one exported list, asserted in three directions by
 * `packages/store/src/repos/sms-trigger.test.ts` and
 * `apps/notifications/src/sms/templates.test.ts` —
 *
 *  1. the **trigger's** `WHEN` clause names exactly these types, no more and no
 *     fewer (a dropped type is a message nobody is ever sent; an extra one is a
 *     delivery row the drain has no template for);
 *  2. an event of **each** type, carrying a phone identity, actually seeds a
 *     row — so the list cannot be right about a type the function refuses;
 *  3. `buildSmsMessage` has a **builder** for each — so a type that reaches the
 *     queue cannot exhaust there as *"no SMS template for event type …"*.
 *
 * It is not itself the source of truth for the database — a migration is, and
 * has to be — it is the thing that makes a disagreement fail a test rather than
 * reach a guest.
 */
export const SMS_TRIGGER_EVENT_TYPES = [
    /** The booking site's one-time code (0013). Transactional; §2.6's exemption. */
    'otp.requested',
    /** A garment is on its way (0028). */
    'parcel.shipped_notice',
    /** Her photographs are ready to claim (0030). */
    'album.ready_notice',
    /** The evening before (0037, E1). */
    'appointment.reminder_notice',
    /** Confirmed, declined, moved or lapsed (0040, A5). */
    'appointment.request_notice',
    /** A text to the *owner* about her own diary (0037, E2). */
    'owner.alert_notice',
    /** "Reply YES to get reminders" (0041, §2.6.2(c)). */
    'sms.consent_request',
    /** "Your order will be ready about 6:20" (0057, verticals-plan V1 / R4). */
    'order.ready_notice',
    /** "Your 8:30 sailing is cancelled for weather" (0063, verticals-plan V7 / D4). */
    'departure.weather_notice',
];
//# sourceMappingURL=messaging.js.map